import { useMemo, useState } from 'react';

import { getRoot } from 'mobx-easy';
import { Notification, DropdownMenuItem } from '@bringg/react-components';
import { useMonitoringTranslations } from '@bringg-frontend/monitoring';
import { getRootEnv } from '@bringg-frontend/bringg-web-infra';

import { assignDriverValidator } from 'bringg-web/services/tasks/assign-driver-validator';
import useRouteMonitoringStore from 'bringg-web/features/monitoring/routes-monitoring-screen/stores/helpers/use-route-monitoring-store';
import { useStores } from 'bringg-web/recipes';
import { RoutesActionsType } from 'bringg-web/features/monitoring/routes-monitoring-screen/stores/routes-monitoring-actions-store';
import RootStore from 'bringg-web/stores/root-store';
import { View } from '../types';

enum ActionsType {
	assignDriver = 'assignDriver',
	assignVehicle = 'assignVehicle',
	sendToPlanning = 'sendToPlanning',
	sendToDrivers = 'sendToDrivers',
	donePlanning = 'donePlanning'
}
// until we add all the items to both pages
const viewActionItems = {
	[View.routes]: [
		ActionsType.assignDriver,
		ActionsType.assignVehicle,
		ActionsType.sendToPlanning,
		ActionsType.sendToDrivers
	],
	[View.tasks]: [ActionsType.donePlanning]
};
const useActionItems = (routeId: number, view: View, setIsLoading?: (isLoading: boolean) => void) => {
	const { driversStore } = useStores();
	const {
		routesMonitoringStore: { tasksByRun, detailedRuns },
		routeMonitoringActionsStore: {
			readyToExecuteAction,
			allowMovingTasksToPlanningPhase,
			canSendToDrivers,
			allowAssignDriverToTask,
			allowToEditVehicle
		}
	} = useRouteMonitoringStore();
	const {
		sendToDispatch,
		sendToDispatchFail,
		sendToDispatchSuccess,
		sendToPlanning,
		sendToPlanningFail,
		sendToPlanningSuccess,
		assignDriver,
		reassignDriver,
		assignVehicle,
		reassignVehicle
	} = useMonitoringTranslations();

	const { donePlanning, donePlanningSuccess, donePlanningFail } = useMonitoringTranslations();

	const [userAssignerModalState, setUserAssignerModalState] = useState<boolean>(false);
	const [vehicleAssignModalState, setVehicleAssignModalState] = useState<boolean>(false);

	const actionItems = useMemo(() => {
		const routeDetails = getRoot<RootStore>().data.runStore.get(routeId);
		const actions: DropdownMenuItem[] = [];

		if (!routeDetails) return actions;

		const routeTasks = [...(tasksByRun.get(routeId)?.values() || [])];
		const isRouteCompleted = !!routeDetails?.ended_at;
		const hasDriver = !!routeDetails?.user_id;
		const hasVehicle = !!routeDetails?.vehicle_id;

		const assignDriverValidation = assignDriverValidator.isAllowedToAssignSelectedTasks(
			routeTasks,
			allowAssignDriverToTask
		);

		if (!isRouteCompleted && viewActionItems[view].includes(ActionsType.assignDriver)) {
			actions.push({
				key: ActionsType.assignDriver,
				dataTestId: `assign-driver-${routeId}`,
				label: hasDriver ? reassignDriver : assignDriver,
				disabled: !(assignDriverValidation.canAssign && assignDriverValidation.assignableTasks.length > 0),
				onClick: async () => {
					await driversStore.fetchByTeam(routeDetails.team_id);

					setUserAssignerModalState(true);
				}
			});
		}

		if (allowToEditVehicle && !isRouteCompleted && viewActionItems[view].includes(ActionsType.assignVehicle)) {
			actions.push({
				key: ActionsType.assignVehicle,
				dataTestId: `assign-vehicle-${routeId}`,
				label: hasVehicle ? reassignVehicle : assignVehicle,
				onClick: () => {
					setVehicleAssignModalState(true);
				}
			});
		}

		if (
			allowMovingTasksToPlanningPhase &&
			routeDetails.ready_to_execute &&
			!isRouteCompleted &&
			viewActionItems[view].includes(ActionsType.sendToPlanning)
		) {
			actions.push({
				key: ActionsType.sendToPlanning,
				dataTestId: `send-to-planning-${routeId}`,
				label: sendToPlanning,
				onClick: async () => {
					const success = await readyToExecuteAction(RoutesActionsType.MOVE_TO_PLANNING, routeId);

					if (success) {
						Notification.success(sendToPlanningSuccess);
					} else {
						Notification.error(sendToPlanningFail);
					}
				}
			});
		}

		if (!routeDetails.ready_to_execute && viewActionItems[view].includes(ActionsType.donePlanning)) {
			actions.push({
				key: ActionsType.donePlanning,
				dataTestId: `done-planning-${routeId}`,
				label: donePlanning,
				onClick: async () => {
					setIsLoading?.(true);
					const response = await getRootEnv().dashboardSdk.sdk.v2.runs.markDonePlanning([routeId]);
					if (response.success) {
						Notification.success(donePlanningSuccess);
					} else {
						Notification.error(donePlanningFail);
					}
					setIsLoading?.(false);
				}
			});
		}

		if (
			canSendToDrivers &&
			routeDetails.ready_to_execute === false &&
			viewActionItems[view].includes(ActionsType.sendToDrivers)
		) {
			actions.push({
				key: ActionsType.sendToDrivers,
				dataTestId: `send-to-driver-${routeId}`,
				label: sendToDispatch,
				onClick: async () => {
					const success = await readyToExecuteAction(RoutesActionsType.MOVE_TO_DISPATCH, routeId);

					if (success) {
						Notification.success(sendToDispatchSuccess);
					} else {
						Notification.error(sendToDispatchFail);
					}
				}
			});
		}

		return actions;
	}, [routeId, detailedRuns, tasksByRun]);

	return {
		actionItems,
		userAssignerModalState: { open: userAssignerModalState, set: setUserAssignerModalState },
		vehicleAssignModalState: { open: vehicleAssignModalState, set: setVehicleAssignModalState }
	};
};

export default useActionItems;
